/**
 * The narrow left rail: the logo, the agency areas as icon-over-label
 * buttons (Clients, Reports, Roll-Ups, KPIs | Data, Templates, MCP), and the
 * inbox, Settings and the account at the bottom.
 */

import type { ReactNode } from 'react';

import { InboxBell } from '../kpis/InboxDrawer.tsx';
import { hrefFor, type AgencyArea, type Route } from './routes.ts';

const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

const ICONS: Record<AgencyArea, ReactNode> = {
  clients: (
    <svg viewBox="0 0 24 24" {...STROKE}>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 19c.6-3.2 2.8-5 5.5-5s4.9 1.8 5.5 5" />
      <circle cx="17" cy="9" r="2.4" />
      <path d="M16 14.2c2.4.2 4 1.8 4.5 4.8" />
    </svg>
  ),
  reports: (
    <svg viewBox="0 0 24 24" {...STROKE}>
      <path d="M7 3h7l4 4v14H7z" />
      <path d="M14 3v4h4M10 17v-3M13 17v-5M16 17v-2" />
    </svg>
  ),
  rollups: (
    <svg viewBox="0 0 24 24" {...STROKE}>
      <path d="M12 3a9 9 0 1 0 9 9h-9z" />
      <path d="M15 3.5A9 9 0 0 1 20.5 9H15z" />
    </svg>
  ),
  kpis: (
    <svg viewBox="0 0 24 24" {...STROKE}>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4.5" />
      <path d="M12 12l6-6M16 6h2v2" />
    </svg>
  ),
  data: (
    <svg viewBox="0 0 24 24" {...STROKE}>
      <path d="M12 3l9 4.5-9 4.5-9-4.5z" />
      <path d="M3 12l9 4.5 9-4.5M3 16.5L12 21l9-4.5" />
    </svg>
  ),
  templates: (
    <svg viewBox="0 0 24 24" {...STROKE}>
      <rect x="8" y="3" width="12" height="14" rx="2" />
      <path d="M16 21H6a2 2 0 0 1-2-2V7" />
    </svg>
  ),
  mcp: (
    <svg viewBox="0 0 24 24" {...STROKE}>
      <path d="M8 12l4-4a3 3 0 0 1 4 4l-6 6a2 2 0 0 1-3-3l6-6" />
      <path d="M5 9l5-5a3 3 0 0 1 4 0" />
    </svg>
  ),
  settings: (
    <svg viewBox="0 0 24 24" {...STROKE}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5.3 5.3l2.1 2.1M16.6 16.6l2.1 2.1M5.3 18.7l2.1-2.1M16.6 7.4l2.1-2.1" />
    </svg>
  ),
};

const LABELS: Record<AgencyArea, string> = {
  clients: 'Clients',
  reports: 'Reports',
  rollups: 'Roll-Ups',
  kpis: 'KPIs',
  data: 'Data',
  templates: 'Templates',
  mcp: 'MCP',
  settings: 'Settings',
};

const TOP: AgencyArea[] = ['clients', 'reports', 'rollups', 'kpis'];
const LOWER: AgencyArea[] = ['data', 'templates', 'mcp'];

function RailLink({ area, active }: { area: AgencyArea; active: boolean }): ReactNode {
  return (
    <a
      className={`ag-rail-link${active ? ' on' : ''}`}
      href={hrefFor({ area })}
      aria-current={active ? 'page' : undefined}
    >
      <span className="ag-rail-icon" aria-hidden="true">
        {ICONS[area]}
      </span>
      {LABELS[area]}
    </a>
  );
}

export function IconRail({
  organisationId,
  route,
  initials,
  onSignOut,
}: {
  organisationId: string;
  route: Route;
  initials: string;
  onSignOut: () => void;
}): ReactNode {
  // Inside a client the Clients button stays lit: a client is part of Clients.
  const current: AgencyArea = route.area === 'client' ? 'clients' : route.area;
  return (
    <nav className="ag-rail" aria-label="Main">
      <a
        className="ag-rail-logo"
        href={hrefFor({ area: 'clients' })}
        aria-label="SynAnalytics home"
      >
        <img src="/favicon-96.png" alt="" width="40" height="40" />
      </a>
      {TOP.map((area) => (
        <RailLink key={area} area={area} active={current === area} />
      ))}
      <hr className="ag-rail-rule" />
      {LOWER.map((area) => (
        <RailLink key={area} area={area} active={current === area} />
      ))}
      <span className="ag-rail-spacer" />
      <InboxBell organisationId={organisationId} />
      <RailLink area="settings" active={current === 'settings'} />
      <button
        type="button"
        className="ag-rail-avatar"
        title="Sign out"
        aria-label="Sign out"
        onClick={onSignOut}
      >
        {initials}
      </button>
    </nav>
  );
}
